import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import { Button, TextInput } from '@/components/ui/daisy';
import CoursePath from '@/models/CoursePath';
import Fieldset from '../ui/daisy/fieldset';
import CoursePathsField from './CoursePathsField';

export type EvaluationBlockInlineFormFields = {
  name: string;
  coursePaths: CoursePath[];
};

type EvaluationBlockInlineFormProps = {
  defaultValues?: EvaluationBlockInlineFormFields;
  courseLineId?: number;
  onSubmit: (data: EvaluationBlockInlineFormFields) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function EvaluationBlockInlineForm({
  defaultValues,
  courseLineId,
  onSubmit,
  onCancel,
  isLoading,
}: EvaluationBlockInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<EvaluationBlockInlineFormFields>({
    defaultValues: {
      name: defaultValues?.name ?? '',
      coursePaths: defaultValues?.coursePaths ?? [],
    },
  });

  return (
    <FieldsetContainer>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <TextInput.Fieldset
          label={t('blockName')}
          labelAlt={errors?.name?.message}
        >
          <TextInput
            type="text"
            placeholder={t('blockNamePlaceholder')}
            className="w-full"
            color={!errors?.name?.message ? 'default' : 'error'}
            {...register('name', { required: t('blockNameRequired') })}
          />
        </TextInput.Fieldset>
        {courseLineId && (
          <Fieldset label={t('pedagogicalContentSection')}>
            <CoursePathsField control={control} courseLineId={courseLineId} />
          </Fieldset>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="grow bg-base-300"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            className="grow"
            disabled={isLoading || !isDirty}
            loading={isLoading}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FieldsetContainer>
  );
}
